import { Feature, FeatureCollection, Geometry, Point, Position } from "geojson";

import { Dataset } from "@/types/dataset";
import { reorderLayerBasedOnArea } from "@/utils/map";

type UseSiteProfilingLayersProps = {
  handleAddPolygonLayer: (
    dataset: Dataset,
    data: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddMarkers: (
    data: { name?: string; coordinates: [number, number] | Position }[],
    draggable: boolean
  ) => maplibregl.Marker[];
  hideAllMarkers: () => void;
};

const LOCATION_NAME_KEY = "Location Name";

export default function useSiteProfilingLayers({
  handleAddPolygonLayer,
  handleAddMarkers,
  hideAllMarkers
}: UseSiteProfilingLayersProps) {
  const handleAddSiteProfiling = (
    newDataset: Dataset,
    featureCollection: FeatureCollection<Geometry>
  ) => {
    const pointFeaturesArray = featureCollection.features.filter(
      (feature) =>
        feature.geometry?.type === "Point" ||
        feature.geometry?.type === "MultiPoint"
    );

    const uniquePointFeatures = pointFeaturesArray.reduce(
      (acc, current) => {
        const locationId = current.properties?.location_id;
        if (locationId && !acc.seenLocationIds.has(locationId)) {
          acc.seenLocationIds.add(locationId);
          acc.features.push(current);
        } else if (!locationId) {
          // Handle features without location_id if necessary, e.g., add them all
          // Or decide to filter them out if location_id is mandatory for uniqueness
          acc.features.push(current);
        }
        return acc;
      },
      { features: [] as Feature<Geometry>[], seenLocationIds: new Set() }
    ).features;

    const polygonFeatures: FeatureCollection<Geometry> = {
      type: "FeatureCollection",
      features: featureCollection.features.filter(
        (feature) =>
          feature.geometry?.type === "Polygon" ||
          feature.geometry?.type === "MultiPolygon"
      )
    };

    const sortedPolygonFeatures = reorderLayerBasedOnArea(polygonFeatures);

    // If we have polygon features, add a polygon layer
    if (sortedPolygonFeatures.features.length > 0) {
      const polygonDataset = {
        ...newDataset,
        data: {
          ...newDataset.data
        }
      };
      handleAddPolygonLayer(
        polygonDataset,
        sortedPolygonFeatures,
        `${polygonDataset.data.id}-polygon`
      );
    }

    // If we have point features, add a point layer
    if (uniquePointFeatures.length > 0) {
      const items = uniquePointFeatures.map((feature) => {
        const coords = (feature.geometry as Point).coordinates;
        const name = feature.properties?.[LOCATION_NAME_KEY];
        return {
          name,
          coordinates: coords
        };
      });

      handleAddMarkers(items, false);

      if (!newDataset.properties.isVisible) hideAllMarkers();
    }
  };

  return { handleAddSiteProfiling };
}
